// Category CRUD for the caller's own custom leaves (design §8.3, D6). System
// rows (`is_system=true`) are read-only server-side -- a rename/delete
// attempt against one is `403`, surfaced through `ApiError` like every other
// coded error here, never silently swallowed.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope, isErrorEnvelope } from '../envelope';
import { categorySchema, type Category } from './useCategories';

export class ApiError extends Error {
  code: string;
  details: unknown;
  constructor(code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.details = details;
  }
}

function toApiError(err: unknown): ApiError {
  if (isAxiosError(err) && isErrorEnvelope(err.response?.data)) {
    const { error } = err.response!.data as { error: { code: string; message: string; details?: unknown } };
    return new ApiError(error.code, error.message, error.details);
  }
  return new ApiError('unknown', err instanceof Error ? err.message : 'Request failed');
}

export const createCategoryRequestSchema = z.object({
  parent_id: z.string(),
  name: z.string().min(1),
});
export type CreateCategoryRequest = z.infer<typeof createCategoryRequestSchema>;

export const deleteCategoryResultSchema = z.object({ deleted: z.literal(true) });

function invalidateCategories(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['categories'] });
}

/** POST /api/categories -- creates a user leaf under one of the five system parents. */
export function useCreateCategory() {
  const queryClient = useQueryClient();
  return useMutation<Category, ApiError, CreateCategoryRequest>({
    mutationFn: async (input) => {
      try {
        const response = await client.post('/api/categories', input);
        return parseEnvelope(categorySchema, response.data).data;
      } catch (err) {
        throw toApiError(err);
      }
    },
    onSuccess: () => invalidateCategories(queryClient),
  });
}

/** PATCH /api/categories/{id} -- rename own leaf only. */
export function useRenameCategory() {
  const queryClient = useQueryClient();
  return useMutation<Category, ApiError, { categoryId: string; name: string }>({
    mutationFn: async ({ categoryId, name }) => {
      try {
        const response = await client.patch(`/api/categories/${categoryId}`, { name });
        return parseEnvelope(categorySchema, response.data).data;
      } catch (err) {
        throw toApiError(err);
      }
    },
    onSuccess: () => invalidateCategories(queryClient),
  });
}

/**
 * DELETE /api/categories/{id}?reassign_to=<id> -- soft delete. Without
 * `reassignTo`, a category still in use rejects with `ApiError` whose
 * `code === 'category_in_use'` and `details` carrying the live line-item
 * count; the caller decides whether to re-issue with `reassignTo` set,
 * never a silent forced reassignment.
 */
export function useDeleteCategory() {
  const queryClient = useQueryClient();
  return useMutation<{ deleted: true }, ApiError, { categoryId: string; reassignTo?: string }>({
    mutationFn: async ({ categoryId, reassignTo }) => {
      try {
        const response = await client.delete(`/api/categories/${categoryId}`, {
          params: reassignTo ? { reassign_to: reassignTo } : undefined,
        });
        return parseEnvelope(deleteCategoryResultSchema, response.data).data;
      } catch (err) {
        throw toApiError(err);
      }
    },
    onSuccess: () => invalidateCategories(queryClient),
  });
}

/** Convenience bundle -- the §7.4 contract names one hook, `useCategoryMutations`. */
export function useCategoryMutations() {
  return {
    create: useCreateCategory(),
    rename: useRenameCategory(),
    remove: useDeleteCategory(),
  };
}
